import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Star, Quote, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import siteConfig from '@/config/siteConfig';

const TestimonialCard = ({ name, role, quote, rating = 5 }) => (
  <div className="luxury-card-gold rounded-3xl p-8 h-full flex flex-col">
    <div className="flex gap-1 mb-4">
      {Array.from({ length: rating }).map((_, i) => (
        <Star key={i} className="w-5 h-5 fill-accent text-accent" />
      ))}
    </div>
    <Quote className="w-8 h-8 text-accent/30 mb-4" />
    <p className="text-foreground font-body leading-relaxed flex-grow mb-6 text-sm md:text-base">"{quote}"</p>
    <div className="border-t border-border/30 pt-4">
      <p className="font-display text-foreground">{name}</p>
      <p className="text-muted-foreground text-sm font-body">{role}</p>
    </div>
  </div>
);

const VideoTestimonial = ({ videoUrl, title, name, initials }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Load video on hover to show preview
  const handleMouseEnter = () => {
    setShowControls(true);
    if (videoRef.current && !videoLoaded) {
      videoRef.current.load();
      videoRef.current.currentTime = 0.5;
    }
  };

  const handleLoadedData = () => {
    setVideoLoaded(true);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div 
      className="relative rounded-2xl overflow-hidden border border-accent/20 shadow-gold aspect-[9/16] bg-gradient-to-br from-card to-background-secondary cursor-pointer group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowControls(false)}
      onClick={togglePlay}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        src={videoUrl}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        muted={isMuted}
        playsInline
        loop
        preload="none"
        onLoadedData={handleLoadedData}
        onEnded={() => setIsPlaying(false)}
      />
      
      {/* Placeholder with name - shown before video loads */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-primary-light/50 to-background transition-opacity duration-300 ${videoLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        {/* Avatar circle with initials */}
        <div className="w-24 h-24 rounded-full bg-accent/20 border-2 border-accent/40 flex items-center justify-center mb-4 shadow-gold">
          <span className="text-3xl font-display text-gradient-gold">{initials}</span>
        </div>
        <p className="text-foreground font-display text-lg">{name}</p>
        <p className="text-muted-foreground font-body text-sm mt-1">Survoler pour prévisualiser</p>
      </div>
      
      {/* Overlay gradient */}
      <div className={`absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent transition-opacity duration-300 ${isPlaying && !showControls ? 'opacity-0' : 'opacity-100'}`} />
      
      {/* Play/Pause button */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isPlaying && !showControls ? 'opacity-0' : 'opacity-100'}`}>
        <motion.button
          className="w-16 h-16 rounded-full bg-accent flex items-center justify-center shadow-gold-intense hover:scale-110 transition-transform"
          whileTap={{ scale: 0.95 }}
        >
          {isPlaying ? (
            <Pause className="w-7 h-7 text-background" />
          ) : (
            <Play className="w-8 h-8 text-background ml-1" />
          )}
        </motion.button>
      </div>

      {/* Title */}
      <div className={`absolute bottom-0 left-0 right-0 p-4 transition-opacity duration-300 ${isPlaying && !showControls ? 'opacity-0' : 'opacity-100'}`}>
        <p className="text-foreground font-body text-sm bg-background/60 rounded-lg p-2 backdrop-blur-sm inline-block">
          {title}
        </p>
      </div>

      {/* Mute button */}
      <button
        onClick={toggleMute}
        className={`absolute top-4 right-4 w-10 h-10 rounded-full bg-background/60 backdrop-blur-sm flex items-center justify-center hover:bg-background/80 transition-all duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5 text-foreground" />
        ) : (
          <Volume2 className="w-5 h-5 text-foreground" />
        )}
      </button>
    </div>
  );
};

const ScreenshotTestimonial = ({ src, alt }) => (
  <motion.div 
    className="rounded-2xl overflow-hidden border border-accent/20 shadow-md hover:shadow-gold transition-shadow duration-500"
    whileHover={{ scale: 1.02 }}
  >
    <img src={src} alt={alt} className="w-full h-auto" />
  </motion.div>
);

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentSlide, setCurrentSlide] = useState(0);

  const mainTestimonials = [
    {
      name: "Sandrine M.",
      role: "Juriste en reconversion",
      quote: "C'est un nouveau départ pour moi, à un moment de ma vie où je devais faire un reset total dans tous les domaines. J'ai trouvé auprès des dauphins et de Gilles le soutien dont j'avais besoin pour poser de nouvelles fondations solides et construire ma nouvelle part de vie avec confiance et fluidité"
    },
    {
      name: "Natacha Pradier",
      role: "Coach en développement personnel",
      quote: "J'ai fait beaucoup de formation et souvent je n'allais pas au bout. Pas assez de sens, de profondeur, de pertinence. Après 8 mois je peux affirmer que les changements sont au rdv et que je me métamorphose. Au-delà de techniques, je trouve que tu nous apportes une philosophie de vie profonde, des éclairages précieux, sans jamais prendre le pouvoir..."
    },
    {
      name: "Luce A.",
      role: "Retraitée",
      quote: "Retraitée depuis 10 ans, j'ai longuement hésité à m'engager. À quoi bon, ma vie est derrière moi... Je n'aime pas l'ordinateur... Toutes les raisons de ne pas faire qui n'ont finalement pas eu raison. Et je me félicite d'avoir contré le mental car je me retrouve. Mon quotidien pétille à nouveau, je ressens beaucoup de paix et de joie."
    }
  ];

  // Video testimonials from the website with custom thumbnails
  const videoTestimonials = [
    { 
      videoUrl: "https://gillesdelieuze.com/wp-content/uploads/2026/01/emd-nathalie.mp4", 
      title: "Témoignage de Nathalie",
      name: "Nathalie",
      initials: "N"
    },
    { 
      videoUrl: "https://gillesdelieuze.com/wp-content/uploads/2025/12/2123088E-00DE-4173-B2F6-C391D26F40E7-2.mov", 
      title: "Mon expérience avec l'école",
      name: "Témoignage",
      initials: "T"
    },
    { 
      videoUrl: "https://gillesdelieuze.com/wp-content/uploads/2026/01/Temoignage-Aline-3.mp4", 
      title: "Témoignage d'Aline",
      name: "Aline",
      initials: "A"
    },
    { 
      videoUrl: "https://gillesdelieuze.com/wp-content/uploads/2026/01/Temoignage-Nelly.mp4", 
      title: "Témoignage de Nelly",
      name: "Nelly",
      initials: "NE"
    },
  ];

  const screenshots = [
    { src: "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1478-300x221.jpg", alt: "Témoignage Brigitte" },
    { src: "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1479-219x300.jpg", alt: "Témoignage Annie" },
    { src: "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1480-300x236.jpg", alt: "Témoignage Nathalie" },
    { src: "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1481-300x283.jpg", alt: "Témoignage Sky light" },
    { src: "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1482-1024x666.jpg", alt: "Témoignage groupe" },
    { src: "https://gillesdelieuze.com/wp-content/uploads/2024/11/Capture-decran-2024-11-07-a-12.15.22-1024x273.png", alt: "Capture témoignage" }
  ];

  const scrollScreenshots = [
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/Capture-decran-2024-11-14-a-07.16.52-1024x521.jpg",
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/Capture-decran-2024-11-14-a-07.14.48-1024x464.jpg",
    "https://gillesdelieuze.com/wp-content/uploads/2025/03/IMG_7748-624x1024.jpg",
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/2Grey-Blue-Modern-New-Collection-Announcement-Instagram-Post-1024x658.jpg",
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/Capture-decran-2024-11-24-a-12.54.44-1024x597.png",
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1388.jpg",
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/IMG_1384.png",
    "https://gillesdelieuze.com/wp-content/uploads/2024/11/1731570650513-2-1024x414.jpg"
  ];

  // Auto-scroll testimonials carousel
  useEffect(() => {
    const totalSlides = mainTestimonials.length;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(interval);
  }, [mainTestimonials.length]);

  return (
    <section id="temoignages" ref={ref} className="section-padding bg-background relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-stellar" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-gold-luminous font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Transformations
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            Ils ont transformé leur vie grâce à l'énergie des 
            <span className="text-gradient-gold"> Maîtres Dauphins !</span>
          </h2>
          <p className="text-muted-foreground font-body text-lg">
            Dès que tu te connectes aux fréquences delphiques, la magie opère ! ✨
          </p>
        </motion.div>

        {/* Video Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h3 className="font-display text-2xl text-foreground text-center mb-8">
            <span className="text-gradient-gold">Témoignages vidéo</span>
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {videoTestimonials.map((video, index) => (
              <motion.div
                key={video.videoUrl}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <VideoTestimonial {...video} />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Main Testimonials Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <div className="relative overflow-hidden">
            <div 
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {mainTestimonials.map((testimonial, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <div className="max-w-3xl mx-auto">
                    <TestimonialCard {...testimonial} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation dots */}
          <div className="flex justify-center gap-3 mt-8">
            {mainTestimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-accent w-8 shadow-gold' 
                    : 'bg-muted hover:bg-accent/50'
                }`}
              />
            ))}
          </div>
        </motion.div>

        {/* Screenshots Grid - with dynamic scrolling */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h3 className="font-display text-2xl text-foreground text-center mb-8">
            Ceux qui l'ont vécu en parlent mieux que moi
          </h3>

          {/* Horizontal scroll container */}
          <div className="relative overflow-hidden">
            <motion.div
              className="flex gap-6"
              animate={{ x: [0, -1500, 0] }}
              transition={{ 
                duration: 40, 
                repeat: Infinity, 
                ease: "linear" 
              }}
            >
              {[...scrollScreenshots, ...scrollScreenshots].map((src, index) => (
                <div key={index} className="flex-shrink-0 w-72 md:w-80">
                  <ScreenshotTestimonial src={src} alt={`Témoignage ${index}`} />
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Screenshots Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          {screenshots.map((screenshot, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.7 + index * 0.05 }}
            >
              <ScreenshotTestimonial {...screenshot} />
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
          className="text-center mt-16"
        >
          <div className="glass-gold rounded-3xl p-8 md:p-12 max-w-3xl mx-auto shadow-gold">
            <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4">
              Ta joie n'a jamais disparu.
            </h3>
            <p className="text-xl text-gold-luminous mb-8">
              Les Dauphins t'attendent pour la réveiller.
            </p>
            <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer">
              <Button className="btn-gold rounded-full px-10 py-6 text-lg">
                JE PLONGE
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
